import {
	RiAlertLine,
	RiArrowDownSLine,
	RiArrowGoBackLine,
	RiArrowUpDownLine,
	RiArrowUpSLine,
	RiDeleteBinLine,
	RiEdit2Line,
	RiFilterLine,
	RiHistoryLine,
	RiImage2Line,
	RiLoader4Line,
	RiPriceTag3Line,
	RiSearchLine,
} from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDebounce } from "@/hooks/use-debounce";
import { applyColumnOrder } from "@/lib/column-order";
import { isRangeToken } from "@/lib/range-token";
import { cn } from "@/lib/utils";
import { FilterBar } from "./filter-bar";

export const Route = createFileRoute("/_app/catalogue/editor")({
	component: CatalogueEditorPage,
});

// ---------------------------------------------------------------------------
// Types (camelCase matching backend shapes)
// ---------------------------------------------------------------------------

interface OverrideInfo {
	overrideValue: string;
	originalValue: string;
	editedAt: string;
	editedBy: string | null;
}

interface CatalogueRow {
	id: string;
	articleId: string;
	articleSizeId: string;
	targetPlatform: string;
	values: Record<string, string>;
	overrides: Record<string, OverrideInfo>;
	commercialDiscount: number | null;
	hasMissingMandatory: boolean;
	exportEnabled: boolean;
	stockQuantity: number;
	totalArticleStock: number;
	lastRebuiltAt: string;
	articleNumber: string;
	supplierArticleNumber: string | null;
	articleName: string;
	size: string;
	ean: string | null;
}

interface ListResponse {
	rows: CatalogueRow[];
	totalCount: number;
	page: number;
	pageSize: number;
}

interface DiscountAverage {
	weightedAverage: number | null;
	simpleAverage: number | null;
	rowCount: number;
	rowsWithoutDiscount: number;
	exportedTotal: number;
	exportedMissing: number;
}

interface TargetColumn {
	key: string;
	label: string;
	mandatory: boolean;
	autoMapped?: boolean;
	width?: string;
}

/// Per-user preference key holding the Editor's column order (array of
/// column keys). Stored server-side via GET/PUT /api/v1/preferences/{key}
/// so the arrangement follows the user across devices.
const COLUMN_ORDER_PREF_KEY = "editor.columnOrder.farfetch";

/// Frontend-only width hints per column key; everything else about a
/// column (label, effective mandatory, autoMapped) comes from
/// `GET /api/v1/catalogue/target-columns` so the Editor can't drift
/// from the backend registry or the org's per-column overrides.
const COLUMN_WIDTHS: Record<string, string> = {
	local_market_price: "min-w-[140px]",
	product_name: "min-w-[180px]",
	item_description: "min-w-[200px]",
	additional_details: "min-w-[180px]",
	size_and_fit_details: "min-w-[160px]",
	composition: "min-w-[160px]",
};

/// Static fallback used only until the target-columns query resolves
/// (or if it fails): mandatory flags are the registry DEFAULTS here,
/// not the org's effective values — the fetched list replaces this.
const FALLBACK_TARGET_COLUMNS: TargetColumn[] = [
	{ key: "brand", label: "Brand", mandatory: true },
	{ key: "brand_product_id", label: "Brand Product ID", mandatory: true },
	{ key: "partner_product_id", label: "Partner Product ID", mandatory: false },
	{ key: "brand_color_id", label: "Brand Color ID", mandatory: true },
	{ key: "season", label: "Season", mandatory: true },
	{ key: "collection", label: "Collection", mandatory: true },
	{ key: "gender", label: "Gender", mandatory: false },
	{ key: "category", label: "Category", mandatory: true },
	{ key: "size_scale", label: "Size Scale", mandatory: true },
	{ key: "size", label: "Size", mandatory: true, autoMapped: true },
	{ key: "stock", label: "Stock", mandatory: true, autoMapped: true },
	{ key: "local_market_price", label: "Local Market Price", mandatory: true },
	{ key: "barcode", label: "Barcode", mandatory: true },
	{ key: "product_name", label: "Product Name", mandatory: false },
	{ key: "item_description", label: "Item Description", mandatory: false },
	{ key: "additional_details", label: "Additional Details", mandatory: false },
	{ key: "size_and_fit_details", label: "Size and Fit", mandatory: false },
	{ key: "made_in", label: "Made In", mandatory: false },
	{ key: "composition", label: "Composition", mandatory: false },
].map((c) => ({ ...c, width: COLUMN_WIDTHS[c.key] }));

/// Columns the backend can sort by. Keys match SortKey::parse in
/// backend/src/routes/catalogue_rows.rs (size is intentionally NOT
/// sortable because it would sort lexicographically; "10" before "2").
const SORTABLE_COLUMNS = new Set<string>([
	"article_number",
	"supplier_article_number",
	"article_name",
	"stock_quantity",
	"total_article_stock",
	"commercial_discount",
	"last_rebuilt_at",
	"brand",
	"category",
	"season",
	"local_market_price",
]);

type SortDir = "asc" | "desc";

function formatPct(v: number | null | undefined): string {
	if (v == null) return "—";
	return `${v.toFixed(1)}%`;
}

/// Escape a string for safe use inside a RegExp.
function escapeRegExp(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/// Render `text` with every case-insensitive occurrence of any token
/// in `term` wrapped in a yellow `<mark>` so users can see what their
/// search matched. `term` may be a single word, a whitespace-separated
/// multi-token string (multi-token search splits on whitespace and
/// AND-combines on the backend; the highlighter shows every match for
/// every token). Returns the raw text when `term` is empty or no
/// tokens match.
function Highlight({ text, term }: { text: string | null | undefined; term: string }) {
	if (!text) return null;
	if (!term) return <>{text}</>;
	// Split into non-empty tokens; ignore consecutive whitespace.
	const tokens = term
		.split(/\s+/)
		.map((t) => t.trim())
		.filter((t) => t.length > 0)
		// Range tokens (`1000-2000`) match numerically server-side;
		// there is no literal substring to mark.
		.filter((t) => !isRangeToken(t));
	if (tokens.length === 0) return <>{text}</>;
	// Combined regex with alternation so a single `split` walks the
	// string once. Each token is regex-escaped to neutralise user
	// input that contains `*` `?` `(` etc.
	const pattern = tokens.map(escapeRegExp).join("|");
	const parts = text.split(new RegExp(`(${pattern})`, "gi"));
	const lowerTokens = new Set(tokens.map((t) => t.toLowerCase()));
	return (
		<>
			{parts.map((part, i) =>
				lowerTokens.has(part.toLowerCase()) ? (
					<mark key={i} className="bg-yellow-400/40 text-foreground rounded-[2px] px-0.5">
						{part}
					</mark>
				) : (
					<span key={i}>{part}</span>
				),
			)}
		</>
	);
}

function SortableHeader({
	label,
	columnKey,
	sortBy,
	sortDir,
	onToggle,
}: {
	label: string;
	columnKey: string;
	sortBy: string | null;
	sortDir: SortDir;
	onToggle: (key: string) => void;
}) {
	const sortable = SORTABLE_COLUMNS.has(columnKey);
	if (!sortable) return <>{label}</>;
	const active = sortBy === columnKey;
	return (
		<button
			type="button"
			onClick={() => onToggle(columnKey)}
			className="inline-flex items-center gap-1 font-medium hover:text-foreground transition-colors"
		>
			<span>{label}</span>
			{active ? (
				sortDir === "asc" ? (
					<RiArrowUpSLine className="size-3 shrink-0" />
				) : (
					<RiArrowDownSLine className="size-3 shrink-0" />
				)
			) : (
				<RiArrowUpDownLine className="size-3 shrink-0 text-muted-foreground/40" />
			)}
		</button>
	);
}

/// SearchScope is just `string` now - the backend validates whether the
/// value is a known column. Keeping it loose on the FE means a new
/// FALLBACK_TARGET_COLUMNS entry shows up in the dropdown without further code
/// changes.
type SearchScope = string;

interface ScopeOption {
	value: SearchScope;
	label: string;
}

/// Hand-curated article/size column scopes (these search SQL columns
/// directly, not `computed_values` keys).
const ARTICLE_SCOPES: ScopeOption[] = [
	{ value: "article_number", label: "Article number" },
	{ value: "supplier_article_number", label: "Supplier article number" },
	{ value: "article_name", label: "Article name" },
	{ value: "size", label: "Size" },
	{ value: "ean", label: "Barcode (EAN)" },
	{ value: "category_number", label: "Category number" },
	{ value: "brand_number", label: "Brand number" },
	{ value: "season_number", label: "Season number" },
];

/// All FALLBACK_TARGET_COLUMNS entries become Farfetch-column scopes whose value
/// is the column's `key`. The backend resolves these to
/// `computed_values ->> '<key>'` after allowlist validation.
function buildScopeOptions(): ScopeOption[] {
	return [
		{ value: "all", label: "All columns" },
		...ARTICLE_SCOPES,
		...FALLBACK_TARGET_COLUMNS.map((c) => ({
			value: c.key,
			label: c.label,
		})).filter((opt) => !ARTICLE_SCOPES.some((a) => a.value === opt.value)),
	];
}

const SEARCH_SCOPE_LABELS: ScopeOption[] = buildScopeOptions();

function discountColor(v: number | null | undefined): string {
	if (v == null) return "text-muted-foreground";
	if (v >= 28 && v <= 32) return "text-green-600";
	if ((v >= 25 && v < 28) || (v > 32 && v <= 35)) return "text-yellow-600";
	return "text-red-600";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function CatalogueEditorPage() {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	// (Removed) Export / Completeness / Brand / Stock single-column
	// filter inputs. They were superseded by the rule-builder + saved
	// views (commits 1be76a4, 25a89f5). The backend query params still
	// exist on /api/v1/catalogue/rows for URL-bookmark compat.
	const [page, setPage] = useState(1);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [editRow, setEditRow] = useState<CatalogueRow | null>(null);
	const [imageRow, setImageRow] = useState<CatalogueRow | null>(null);
	const [bulkDialog, setBulkDialog] = useState(false);
	const [historyOpen, setHistoryOpen] = useState(false);
	const [brandDiscountsOpen, setBrandDiscountsOpen] = useState(false);

	// Inline cell editing
	const [editingCell, setEditingCell] = useState<{ rowId: string; column: string } | null>(null);
	const [cellValue, setCellValue] = useState("");
	const cellInputRef = useRef<HTMLInputElement>(null);

	const [pageSize, setPageSize] = useState(50);
	const [sortBy, setSortBy] = useState<string | null>(null);
	const [sortDir, setSortDir] = useState<SortDir>("asc");
	const [searchScope, setSearchScope] = useState<SearchScope>("all");
	const debouncedSearch = useDebounce(search, 200);

	// Rule-builder expression. Null = no advanced filter; the legacy
	// dropdowns + search still work independently and AND-combine on
	// the backend.
	const [filterExpr, setFilterExpr] = useState<string | null>(null);

	const pageSizeOptions = [25, 50, 100, 250, 500];

	function toggleSort(key: string) {
		if (!SORTABLE_COLUMNS.has(key)) return;
		if (sortBy === key) {
			if (sortDir === "asc") {
				setSortDir("desc");
			} else {
				// asc -> desc -> none
				setSortBy(null);
				setSortDir("asc");
			}
		} else {
			setSortBy(key);
			setSortDir("asc");
		}
		setPage(1);
	}

	const queryParams = useMemo(() => {
		const q: Record<string, string | number | boolean> = {
			targetPlatform: "farfetch",
			page,
			pageSize,
		};
		if (debouncedSearch) q.search = debouncedSearch;
		if (sortBy) {
			q.sortBy = sortBy;
			q.sortDir = sortDir;
		}
		if (searchScope !== "all") q.searchScope = searchScope;
		if (filterExpr) q.filterExpr = filterExpr;
		return q;
	}, [debouncedSearch, page, pageSize, sortBy, sortDir, searchScope, filterExpr]);

	const { data, isLoading, error } = $api.useQuery(
		"get",
		"/api/v1/catalogue/rows" as never,
		{ params: { query: queryParams as Record<string, string> } } as never,
	);
	const list = data as ListResponse | undefined;
	const rows = list?.rows ?? [];
	const total = list?.totalCount ?? 0;
	const totalPages = Math.ceil(total / pageSize);

	// Target columns + primary-identifier choice from Matching. The
	// Editor's identity column (position 3, right after Export) shows
	// whichever column the operator marked as primary. When NO primary
	// is set, the column falls back to the row's internal UUID (r.id)
	// with the label "UUID" so the operator always has a unique row
	// anchor even before configuring a primary.
	const { data: targetColumnsData } = $api.useQuery(
		"get",
		"/api/v1/catalogue/target-columns" as never,
		{ params: { query: { target: "farfetch" } } } as never,
	);
	const targetColumnsList =
		(targetColumnsData as
			| Array<{
					key: string;
					displayName: string;
					mandatory: boolean;
					autoMapped: boolean;
					isPrimaryIdentifier: boolean;
			  }>
			| undefined) ?? [];
	const primaryColumn = targetColumnsList.find((c) => c.isPrimaryIdentifier);
	// `null` here means "no primary set" → identity column shows r.id
	// with the UUID label, and the column list is NOT filtered (no risk
	// of duplicating any target column).
	const primaryColumnKey: string | null = primaryColumn?.key ?? null;
	const primaryColumnLabel: string = primaryColumn?.displayName ?? "UUID";
	// Columns come from the backend registry (effective per-org
	// `mandatory` incl. Required overrides from Matching); the static
	// list is only the pre-fetch fallback.
	const registryColumns: TargetColumn[] =
		targetColumnsList.length > 0
			? targetColumnsList.map((c) => ({
					key: c.key,
					label: c.displayName,
					mandatory: c.mandatory,
					autoMapped: c.autoMapped,
					width: COLUMN_WIDTHS[c.key],
				}))
			: FALLBACK_TARGET_COLUMNS;
	// Size + stock render as dedicated editable columns on the left
	// (their pre-2026-07 read-only twins showed the same values and
	// confused operators); the primary identifier renders as the
	// identity column. Hide all three from the right-side loop so each
	// value appears exactly once.
	const sizeColumn: TargetColumn = registryColumns.find((c) => c.key === "size") ?? {
		key: "size",
		label: "Size",
		mandatory: true,
	};
	const stockColumn: TargetColumn = registryColumns.find((c) => c.key === "stock") ?? {
		key: "stock",
		label: "Stock",
		mandatory: true,
	};
	const visibleTargetColumns = registryColumns.filter(
		(c) => !c.autoMapped && c.key !== primaryColumnKey,
	);

	// ── Per-user column order (drag a header to rearrange) ──────────────
	// Reorderable set = stock + size + the remaining target columns as ONE
	// ordered list; the fixed prefix (select, Export, identity, Discount)
	// stays put. Saved per user via the preferences endpoint; unknown keys
	// are dropped and new registry columns append at the end (see
	// lib/column-order.ts).
	const { data: columnOrderPref } = $api.useQuery(
		"get",
		"/api/v1/preferences/{key}" as never,
		{ params: { path: { key: COLUMN_ORDER_PREF_KEY } } } as never,
	);
	const savedColumnOrder: string[] | null = (() => {
		const v = (columnOrderPref as { value?: unknown } | undefined)?.value;
		if (!Array.isArray(v)) return null;
		return v.filter((k): k is string => typeof k === "string");
	})();
	// Local override so drags apply instantly; `[]` means "explicitly
	// back to the default" after a reset (distinct from `null` = no
	// local interaction yet, follow the fetched preference).
	const [localColumnOrder, setLocalColumnOrder] = useState<string[] | null>(null);
	const effectiveColumnOrder = localColumnOrder ?? savedColumnOrder;
	const reorderableColumns: TargetColumn[] = [stockColumn, sizeColumn, ...visibleTargetColumns];
	const orderedColumns = applyColumnOrder(reorderableColumns, effectiveColumnOrder);
	const hasCustomColumnOrder = (effectiveColumnOrder?.length ?? 0) > 0;
	const [dragColumnKey, setDragColumnKey] = useState<string | null>(null);
	const [dropColumnKey, setDropColumnKey] = useState<string | null>(null);

	async function persistColumnOrder(order: string[] | null) {
		const { error } = await fetchClient.PUT(
			"/api/v1/preferences/{key}" as never,
			{ params: { path: { key: COLUMN_ORDER_PREF_KEY } }, body: { value: order } } as never,
		);
		if (error) toast.error("Saving column order failed");
	}

	/// Drop the dragged column immediately BEFORE the drop target.
	function handleColumnDrop(targetKey: string) {
		if (!dragColumnKey || dragColumnKey === targetKey) return;
		const keys = orderedColumns.map((c) => c.key).filter((k) => k !== dragColumnKey);
		const at = keys.indexOf(targetKey);
		keys.splice(at < 0 ? keys.length : at, 0, dragColumnKey);
		setLocalColumnOrder(keys);
		void persistColumnOrder(keys);
	}

	const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

	/// Runs AFTER the confirm dialog. The previous order is captured for
	/// the toast's Undo action - a reset is never fatal (the preference
	/// row is deleted server-side and can't be recovered otherwise).
	function resetColumnOrder() {
		const previous = orderedColumns.map((c) => c.key);
		setLocalColumnOrder([]);
		void persistColumnOrder(null);
		toast.success("Column order reset", {
			action: {
				label: "Undo",
				onClick: () => {
					setLocalColumnOrder(previous);
					void persistColumnOrder(previous);
				},
			},
		});
	}

	const { data: discountData } = $api.useQuery(
		"get",
		"/api/v1/catalogue/discount-average" as never,
		{ params: { query: { targetPlatform: "farfetch" } } } as never,
	);
	const discount = discountData as DiscountAverage | undefined;

	useEffect(() => {
		if (editingCell && cellInputRef.current) {
			cellInputRef.current.focus();
			cellInputRef.current.select();
		}
	}, [editingCell]);

	function invalidate() {
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/catalogue/rows"],
		});
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/catalogue/discount-average"],
		});
	}

	function toggleSelect(id: string, checked: boolean) {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (checked) next.add(id);
			else next.delete(id);
			return next;
		});
	}

	function toggleSelectAllOnPage(checked: boolean) {
		if (checked) {
			setSelectedIds(new Set(rows.map((r) => r.id)));
		} else {
			setSelectedIds(new Set());
		}
	}

	async function patchRow(id: string, body: Record<string, unknown>): Promise<boolean> {
		const { error } = await fetchClient.PATCH(
			"/api/v1/catalogue/rows/{id}" as never,
			{
				params: { path: { id } },
				body,
			} as never,
		);
		if (error) {
			toast.error("Update failed");
			return false;
		}
		invalidate();
		return true;
	}

	async function handleExportToggle(row: CatalogueRow, next: boolean) {
		const ok = await patchRow(row.id, { exportEnabled: next });
		if (ok) {
			toast.success(next ? "Marked for export" : "Removed from export");
		}
	}

	function startCellEdit(row: CatalogueRow, column: string) {
		setEditingCell({ rowId: row.id, column });
		setCellValue(row.values[column] ?? "");
	}

	function cancelCellEdit() {
		setEditingCell(null);
	}

	async function saveCellEdit() {
		if (!editingCell) return;
		const row = rows.find((r) => r.id === editingCell.rowId);
		if (!row) {
			setEditingCell(null);
			return;
		}
		const current = row.values[editingCell.column] ?? "";
		if (cellValue === current) {
			setEditingCell(null);
			return;
		}
		const ok = await patchRow(row.id, {
			overrides: { [editingCell.column]: cellValue },
		});
		if (ok) toast.success("Saved");
		setEditingCell(null);
	}

	function handleCellKeyDown(e: React.KeyboardEvent) {
		if (e.key === "Enter") {
			e.preventDefault();
			void saveCellEdit();
		} else if (e.key === "Escape") {
			e.preventDefault();
			cancelCellEdit();
		}
	}

	const allOnPageSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

	return (
		<TooltipProvider>
			<div className="space-y-6">
				<div className="flex items-start justify-between">
					<div>
						<h1 className="text-2xl font-semibold tracking-tight">Catalogue Editor</h1>
						<p className="text-sm text-muted-foreground">
							Edit rows inline. Mark for Farfetch export. Edits survive syncs.
						</p>
					</div>
					<div className="flex gap-2">
						<Button variant="outline" size="sm" onClick={() => setBrandDiscountsOpen(true)}>
							<RiPriceTag3Line className="size-4 mr-1.5" />
							Brand defaults
						</Button>
						<Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)}>
							<RiHistoryLine className="size-4 mr-1.5" />
							History
						</Button>
						{hasCustomColumnOrder && (
							<Button variant="outline" size="sm" onClick={() => setResetConfirmOpen(true)}>
								<RiArrowGoBackLine className="size-4 mr-1.5" />
								Reset columns
							</Button>
						)}
						<AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Reset column order?</AlertDialogTitle>
									<AlertDialogDescription>
										Your custom column arrangement will be removed and the default order restored.
										This only affects your own view. You can Undo right after from the toast - once
										that is gone, the arrangement cannot be recovered.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>Cancel</AlertDialogCancel>
									<AlertDialogAction
										onClick={() => {
											setResetConfirmOpen(false);
											resetColumnOrder();
										}}
									>
										Reset
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				</div>

				{/* Discount widget */}
				<Card>
					<CardContent className="py-4 flex flex-wrap items-center gap-6">
						<div>
							<div className="text-xs text-muted-foreground">Commercial discount</div>
							<div
								className={cn(
									"text-2xl font-semibold tabular-nums",
									discountColor(discount?.weightedAverage),
								)}
							>
								{formatPct(discount?.weightedAverage)}
							</div>
							<div className="text-xs text-muted-foreground">weighted by stock × price</div>
						</div>
						<div>
							<div className="text-xs text-muted-foreground">Simple mean</div>
							<div className="text-lg font-medium tabular-nums">
								{formatPct(discount?.simpleAverage)}
							</div>
						</div>
						<div>
							<div className="text-xs text-muted-foreground">Marked for export</div>
							<div className="text-lg font-medium tabular-nums">{discount?.exportedTotal ?? 0}</div>
						</div>
						<div>
							<div className="text-xs text-muted-foreground">Will export (complete)</div>
							<div className="text-lg font-medium tabular-nums">
								{discount?.rowCount != null && discount?.rowsWithoutDiscount != null
									? discount.rowCount + discount.rowsWithoutDiscount
									: 0}
							</div>
						</div>
						{(discount?.exportedMissing ?? 0) > 0 && (
							<div>
								<div className="text-xs text-muted-foreground">Dropped (missing data)</div>
								<div className="text-lg font-medium tabular-nums text-destructive">
									{discount?.exportedMissing ?? 0}
								</div>
							</div>
						)}
						{(discount?.rowsWithoutDiscount ?? 0) > 0 && (
							<div>
								<div className="text-xs text-muted-foreground">No discount set</div>
								<div className="text-lg font-medium tabular-nums text-yellow-600">
									{discount?.rowsWithoutDiscount ?? 0}
								</div>
							</div>
						)}
						<div className="ml-auto text-xs text-muted-foreground">Target: 28–32%</div>
					</CardContent>
				</Card>

				{/* Saved views + rule-builder toolbar */}
				<Card>
					<CardContent className="pt-6">
						<FilterBar
							expression={filterExpr}
							onExpressionChange={(next) => {
								setFilterExpr(next);
								setPage(1);
							}}
						/>
					</CardContent>
				</Card>

				{/* Filters */}
				<Card>
					<CardContent className="pt-6 flex flex-wrap items-center gap-3">
						<div className="flex items-center gap-2 flex-1 min-w-[280px]">
							<Select
								value={searchScope}
								onValueChange={(v) => {
									setSearchScope(v as SearchScope);
									setPage(1);
								}}
							>
								<SelectTrigger className="w-[180px] shrink-0">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{SEARCH_SCOPE_LABELS.map((s) => (
										<SelectItem key={s.value} value={s.value}>
											{s.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<div className="relative flex-1 min-w-[200px]">
								<RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
								<Input
									placeholder={
										searchScope === "all"
											? "Search any column - `prada 20261` ANDs both, `1000-2000` = number range"
											: `Search ${(SEARCH_SCOPE_LABELS.find((s) => s.value === searchScope)?.label ?? "").toLowerCase()} - multi-token ANDs`
									}
									value={search}
									onChange={(e) => {
										setSearch(e.target.value);
										setPage(1);
									}}
									className="pl-9"
								/>
							</div>
						</div>
						{/* The legacy Export / Completeness / Stock / Brand
						    dropdowns were removed once the rule-builder + saved
						    views shipped. Every one was expressible as a one-
						    click view; the seeded 'Ready to export' and 'Missing
						    data' views cover the two most common cases. The
						    backend query params (brand, exportEnabled,
						    hasMissingMandatory, inStock, articleInStock) stay
						    on /api/v1/catalogue/rows for URL-bookmark + agent
						    compat — they're just not driven from this UI
						    anymore. */}
					</CardContent>
				</Card>

				{/* Bulk action bar */}
				{selectedIds.size > 0 && (
					<Card className="border-primary/40 bg-primary/5">
						<CardContent className="py-3 flex items-center gap-3">
							<span className="text-sm font-medium">{selectedIds.size} selected</span>
							<Button variant="outline" size="sm" onClick={() => setBulkDialog(true)}>
								<RiEdit2Line className="size-4 mr-1.5" />
								Bulk edit
							</Button>
							<Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>
								Clear selection
							</Button>
						</CardContent>
					</Card>
				)}

				{/* Wide inline-editable table */}
				<Card className="overflow-hidden">
					<CardContent className="p-0">
						{isLoading ? (
							<div className="p-6 space-y-3">
								{Array.from({ length: 5 }).map((_, i) => (
									<Skeleton key={i} className="h-12 w-full" />
								))}
							</div>
						) : error ? (
							<div
								role="alert"
								className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2"
							>
								<p className="font-medium">
									Failed to load rows. If you just applied a saved view, its filter may use an
									operator that is no longer valid for that column - edit the view and re-pick the
									operator.
								</p>
								<Button type="button" variant="outline" size="sm" onClick={() => invalidate()}>
									Retry
								</Button>
							</div>
						) : rows.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
								<RiFilterLine className="size-10 mb-3 opacity-50" />
								<p className="text-sm">No rows</p>
								<p className="text-xs mt-1">Run a sync first, or adjust your filters.</p>
							</div>
						) : (
							<div className="catalogue-editor-scroll max-h-[calc(100vh-380px)] overflow-scroll [&_[data-slot=table-container]]:overflow-visible">
								<Table>
									<TableHeader className="sticky top-0 z-10 bg-background">
										<TableRow>
											<TableHead className="w-10">
												<Checkbox
													checked={allOnPageSelected}
													onCheckedChange={(v) => toggleSelectAllOnPage(!!v)}
												/>
											</TableHead>
											<TableHead className="w-20 text-center whitespace-nowrap">Export</TableHead>
											<TableHead className="whitespace-nowrap">
												{/* Row identity column, pinned to position 3 (right
												    after Export). Operator picks which Farfetch
												    target column is the primary identifier in
												    Matching (Primary toggle). When unset, falls
												    back to the row's internal UUID. The matching
												    target column is hidden from the right-side
												    target-column loop to avoid duplication. */}
												<SortableHeader
													label={primaryColumnLabel}
													columnKey={primaryColumnKey ?? "id"}
													sortBy={sortBy}
													sortDir={sortDir}
													onToggle={toggleSort}
												/>
											</TableHead>
											<TableHead className="text-right whitespace-nowrap w-24">
												<SortableHeader
													label="Discount"
													columnKey="commercial_discount"
													sortBy={sortBy}
													sortDir={sortDir}
													onToggle={toggleSort}
												/>
											</TableHead>
											{orderedColumns.map((c) => (
												<TableHead
													key={c.key}
													draggable
													onDragStart={(e) => {
														setDragColumnKey(c.key);
														e.dataTransfer?.setData("text/plain", c.key);
													}}
													onDragOver={(e) => {
														e.preventDefault();
														if (dragColumnKey && dragColumnKey !== c.key) {
															setDropColumnKey(c.key);
														}
													}}
													onDragLeave={() => {
														if (dropColumnKey === c.key) setDropColumnKey(null);
													}}
													onDrop={(e) => {
														e.preventDefault();
														handleColumnDrop(c.key);
														setDragColumnKey(null);
														setDropColumnKey(null);
													}}
													onDragEnd={() => {
														setDragColumnKey(null);
														setDropColumnKey(null);
													}}
													className={cn(
														"whitespace-nowrap cursor-grab select-none",
														c.width ?? "min-w-[120px]",
														dragColumnKey === c.key && "opacity-50",
														dropColumnKey === c.key && "border-l-2 border-l-primary",
													)}
												>
													<span className="flex items-center gap-1.5">
														<SortableHeader
															label={c.label}
															columnKey={c.key === "stock" ? "stock_quantity" : c.key}
															sortBy={sortBy}
															sortDir={sortDir}
															onToggle={toggleSort}
														/>
														{c.mandatory && !c.autoMapped && (
															<Badge variant="secondary" className="text-[10px] px-1 py-0">
																Req
															</Badge>
														)}
													</span>
												</TableHead>
											))}
											<TableHead className="w-10" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{rows.map((r) => (
											<TableRow
												key={r.id}
												className={cn(
													r.hasMissingMandatory && "border-l-2 border-l-destructive/50",
												)}
											>
												<TableCell>
													<Checkbox
														checked={selectedIds.has(r.id)}
														onCheckedChange={(v) => toggleSelect(r.id, !!v)}
													/>
												</TableCell>
												<TableCell className="text-center">
													<Switch
														checked={r.exportEnabled}
														onCheckedChange={(v) => handleExportToggle(r, v)}
													/>
												</TableCell>
												<TableCell className="whitespace-nowrap">
													{/* Identity cell. When a primary is set, show its
													    value; otherwise the row's UUID so the operator
													    always has a unique anchor. */}
													<span className="font-mono text-xs">
														<Highlight
															text={primaryColumnKey ? r.values[primaryColumnKey] || "—" : r.id}
															term={debouncedSearch}
														/>
													</span>
												</TableCell>
												<TableCell
													className={cn(
														"text-right font-mono tabular-nums text-xs",
														discountColor(r.commercialDiscount),
													)}
												>
													{formatPct(r.commercialDiscount)}
												</TableCell>
												{orderedColumns.map((col) => (
													<InlineCell
														key={col.key}
														row={r}
														column={col}
														editing={editingCell?.rowId === r.id && editingCell?.column === col.key}
														editValue={cellValue}
														searchTerm={debouncedSearch}
														onStartEdit={() => startCellEdit(r, col.key)}
														onChangeEdit={setCellValue}
														onSaveEdit={saveCellEdit}
														inputRef={cellInputRef}
														onKeyDown={handleCellKeyDown}
														suffix={
															col.key === "stock" ? (
																<Tooltip>
																	<TooltipTrigger asChild>
																		<span className="text-[10px] text-muted-foreground cursor-help whitespace-nowrap">
																			Σ {r.totalArticleStock}
																		</span>
																	</TooltipTrigger>
																	<TooltipContent side="left">
																		<div className="text-xs">
																			{r.stockQuantity} on this size, {r.totalArticleStock} across
																			all sizes
																		</div>
																	</TooltipContent>
																</Tooltip>
															) : undefined
														}
													/>
												))}
												<TableCell>
													<div className="flex gap-0.5">
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="ghost"
																	size="sm"
																	onClick={() => setImageRow(r)}
																	className="size-8 p-0"
																>
																	<RiImage2Line className="size-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent side="left">Images</TooltipContent>
														</Tooltip>
														<Tooltip>
															<TooltipTrigger asChild>
																<Button
																	variant="ghost"
																	size="sm"
																	onClick={() => setEditRow(r)}
																	className="size-8 p-0"
																>
																	<RiEdit2Line className="size-4" />
																</Button>
															</TooltipTrigger>
															<TooltipContent side="left">Edit all fields</TooltipContent>
														</Tooltip>
													</div>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Pagination */}
				{total > 0 && (
					<div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
						<span>
							Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
						</span>
						<div className="flex items-center gap-3">
							<div className="flex items-center gap-2">
								<span className="text-xs">Rows per page</span>
								<Select
									value={String(pageSize)}
									onValueChange={(v) => {
										const next = Number.parseInt(v, 10);
										setPageSize(next);
										setPage(1);
									}}
								>
									<SelectTrigger size="sm" className="w-20">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{pageSizeOptions.map((n) => (
											<SelectItem key={n} value={String(n)}>
												{n}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={page === 1}
									onClick={() => setPage(page - 1)}
								>
									Previous
								</Button>
								<Button
									variant="outline"
									size="sm"
									disabled={page >= totalPages}
									onClick={() => setPage(page + 1)}
								>
									Next
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Row edit dialog */}
				{editRow && (
					<EditRowDialog
						row={editRow}
						columns={registryColumns}
						onClose={() => setEditRow(null)}
						onSaved={() => {
							invalidate();
							setEditRow(null);
						}}
					/>
				)}

				{/* Images dialog */}
				{imageRow && (
					<ImagesDialog
						articleId={imageRow.articleId}
						articleNumber={imageRow.articleNumber}
						onClose={() => setImageRow(null)}
					/>
				)}

				{/* Bulk edit dialog */}
				{bulkDialog && (
					<BulkEditDialog
						ids={Array.from(selectedIds)}
						columns={registryColumns}
						onClose={() => setBulkDialog(false)}
						onSaved={() => {
							invalidate();
							setBulkDialog(false);
							setSelectedIds(new Set());
						}}
					/>
				)}

				{/* Audit history dialog */}
				{historyOpen && <HistoryDialog onClose={() => setHistoryOpen(false)} />}

				{/* Brand default discounts dialog */}
				{brandDiscountsOpen && (
					<BrandDiscountsDialog
						onClose={() => setBrandDiscountsOpen(false)}
						onChanged={() => invalidate()}
					/>
				)}
			</div>
		</TooltipProvider>
	);
}

// ---------------------------------------------------------------------------
// Inline-editable cell
// ---------------------------------------------------------------------------

function InlineCell({
	row,
	column,
	editing,
	editValue,
	searchTerm,
	onStartEdit,
	onChangeEdit,
	onSaveEdit,
	onKeyDown,
	inputRef,
	suffix,
}: {
	row: CatalogueRow;
	column: TargetColumn;
	editing: boolean;
	editValue: string;
	searchTerm: string;
	onStartEdit: () => void;
	onChangeEdit: (v: string) => void;
	onSaveEdit: () => void;
	onKeyDown: (e: React.KeyboardEvent) => void;
	inputRef: React.RefObject<HTMLInputElement | null>;
	/// Optional static adornment rendered after the value when not
	/// editing (e.g. the stock column's Σ-total hint).
	suffix?: React.ReactNode;
}) {
	const value = row.values[column.key] ?? "";
	const override = row.overrides[column.key];
	const isMissingMandatory = column.mandatory && !value;

	const displayValue = override ? (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className="block truncate cursor-help">
					{value ? <Highlight text={value} term={searchTerm} /> : "—"}
				</span>
			</TooltipTrigger>
			<TooltipContent side="top">
				<div className="text-xs space-y-1">
					<div>
						<span className="text-muted-foreground">Original:</span> {override.originalValue || "—"}
					</div>
					<div className="text-muted-foreground">
						edited {new Date(override.editedAt).toLocaleString()}
					</div>
				</div>
			</TooltipContent>
		</Tooltip>
	) : (
		<span className="block truncate">
			{value ? (
				<Highlight text={value} term={searchTerm} />
			) : (
				<span className="text-muted-foreground italic">{isMissingMandatory ? "missing" : "—"}</span>
			)}
		</span>
	);

	return (
		<TableCell
			className={cn(
				"relative cursor-pointer text-xs max-w-[240px]",
				override && "bg-yellow-500/10",
				isMissingMandatory && "bg-destructive/5",
			)}
			onClick={() => {
				if (!editing) onStartEdit();
			}}
		>
			{editing ? (
				<Input
					ref={inputRef}
					value={editValue}
					onChange={(e) => onChangeEdit(e.target.value)}
					onBlur={onSaveEdit}
					onKeyDown={onKeyDown}
					className="h-7 text-xs -my-1"
				/>
			) : suffix ? (
				<span className="flex items-center gap-1.5">
					<span className="min-w-0">{displayValue}</span>
					{suffix}
				</span>
			) : (
				displayValue
			)}
			{isMissingMandatory && !editing && (
				<RiAlertLine className="absolute top-1 right-1 size-3 text-destructive/60 pointer-events-none" />
			)}
		</TableCell>
	);
}

// ---------------------------------------------------------------------------
// Full edit dialog — all fields + discount + quick image access
// ---------------------------------------------------------------------------

function EditRowDialog({
	row,
	columns,
	onClose,
	onSaved,
}: {
	row: CatalogueRow;
	columns: TargetColumn[];
	onClose: () => void;
	onSaved: () => void;
}) {
	const [values, setValues] = useState<Record<string, string>>(() => ({
		...row.values,
	}));
	const [discount, setDiscount] = useState<string>(
		row.commercialDiscount != null ? String(row.commercialDiscount) : "",
	);
	const [saving, setSaving] = useState(false);

	async function handleSave() {
		setSaving(true);
		try {
			const overrides: Record<string, string | null> = {};
			for (const col of columns) {
				const current = values[col.key] ?? "";
				const original = row.values[col.key] ?? "";
				if (current !== original) {
					overrides[col.key] = current;
				}
			}

			const body: Record<string, unknown> = {};
			if (Object.keys(overrides).length > 0) body.overrides = overrides;

			const discountNum = discount.trim() === "" ? null : Number(discount);
			if (discountNum == null) {
				body.clearCommercialDiscount = true;
			} else if (!Number.isNaN(discountNum)) {
				body.commercialDiscount = discountNum;
			}

			const { error } = await fetchClient.PATCH(
				"/api/v1/catalogue/rows/{id}" as never,
				{ params: { path: { id: row.id } }, body } as never,
			);
			if (error) {
				toast.error("Save failed");
				return;
			}
			toast.success("Row updated");
			onSaved();
		} finally {
			setSaving(false);
		}
	}

	async function handleRevert(columnKey: string) {
		const { error } = await fetchClient.PATCH(
			"/api/v1/catalogue/rows/{id}" as never,
			{
				params: { path: { id: row.id } },
				body: { overrides: { [columnKey]: null } },
			} as never,
		);
		if (error) {
			toast.error("Revert failed");
			return;
		}
		toast.success("Reverted to computed value");
		onSaved();
	}

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						{row.articleNumber}
						{row.size && (
							<Badge variant="secondary" className="font-mono">
								{row.size}
							</Badge>
						)}
					</DialogTitle>
					<DialogDescription>{row.stockQuantity} in stock</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<ArticleImageGallery articleId={row.articleId} />

					<div>
						<Label htmlFor="discount">Commercial discount (%)</Label>
						<Input
							id="discount"
							type="number"
							min={0}
							max={100}
							step={0.1}
							value={discount}
							onChange={(e) => setDiscount(e.target.value)}
							placeholder="e.g. 30"
							className="mt-1"
						/>
						<p className="text-xs text-muted-foreground mt-1">Leave empty to clear.</p>
					</div>

					<div className="space-y-2">
						{columns.map((col) => {
							const hasOverride = col.key in row.overrides;
							const original = row.overrides[col.key]?.originalValue ?? row.values[col.key] ?? "";
							return (
								<div key={col.key} className="space-y-1">
									<div className="flex items-center justify-between text-xs">
										<Label htmlFor={`col-${col.key}`} className="text-xs">
											{col.label}
											{col.mandatory && (
												<Badge variant="outline" className="ml-1 text-[9px]">
													Req
												</Badge>
											)}
											{hasOverride && (
												<Badge className="ml-1 text-[9px] bg-yellow-500/20 text-yellow-700 hover:bg-yellow-500/30">
													Edited
												</Badge>
											)}
										</Label>
										{hasOverride && (
											<Button variant="ghost" size="xs" onClick={() => handleRevert(col.key)}>
												<RiArrowGoBackLine className="size-3 mr-1" />
												Revert
											</Button>
										)}
									</div>
									<Input
										id={`col-${col.key}`}
										value={values[col.key] ?? ""}
										onChange={(e) => setValues((prev) => ({ ...prev, [col.key]: e.target.value }))}
										className={cn(hasOverride && "border-yellow-500/40")}
									/>
									{hasOverride && (
										<p className="text-[10px] text-muted-foreground">Original: {original || "—"}</p>
									)}
								</div>
							);
						})}
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						{saving && <RiLoader4Line className="size-4 mr-1.5 animate-spin" />}
						Save
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Bulk edit dialog
// ---------------------------------------------------------------------------

function BulkEditDialog({
	ids,
	columns,
	onClose,
	onSaved,
}: {
	ids: string[];
	columns: TargetColumn[];
	onClose: () => void;
	onSaved: () => void;
}) {
	const [mode, setMode] = useState<"discount" | "export_on" | "export_off" | "override">(
		"discount",
	);
	const [discount, setDiscount] = useState("");
	const [columnKey, setColumnKey] = useState(columns[0]?.key ?? "brand");
	const [columnValue, setColumnValue] = useState("");
	const [saving, setSaving] = useState(false);

	async function handleApply() {
		setSaving(true);
		try {
			const updates: Record<string, unknown> = {};
			if (mode === "discount") {
				if (discount.trim() === "") {
					updates.clearCommercialDiscount = true;
				} else {
					const n = Number(discount);
					if (Number.isNaN(n)) {
						toast.error("Not a number");
						return;
					}
					updates.commercialDiscount = n;
				}
			} else if (mode === "export_on") {
				updates.exportEnabled = true;
			} else if (mode === "export_off") {
				updates.exportEnabled = false;
			} else if (mode === "override") {
				updates.overrides = { [columnKey]: columnValue || null };
			}

			const { data, error } = await fetchClient.POST(
				"/api/v1/catalogue/rows/bulk-update" as never,
				{ body: { ids, updates } } as never,
			);
			if (error) {
				toast.error("Bulk update failed");
				return;
			}
			const r = data as { updatedCount?: number };
			toast.success(`Updated ${r.updatedCount ?? ids.length} rows`);
			onSaved();
		} finally {
			setSaving(false);
		}
	}

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Bulk edit {ids.length} rows</DialogTitle>
					<DialogDescription>
						Applies to the rows you selected. Override edits stick across syncs.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<Label>Action</Label>
						<Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="discount">Set commercial discount</SelectItem>
								<SelectItem value="export_on">Mark for export</SelectItem>
								<SelectItem value="export_off">Remove from export</SelectItem>
								<SelectItem value="override">Override column value</SelectItem>
							</SelectContent>
						</Select>
					</div>

					{mode === "discount" && (
						<div>
							<Label htmlFor="bulk-discount">Discount (%)</Label>
							<Input
								id="bulk-discount"
								type="number"
								min={0}
								max={100}
								step={0.1}
								value={discount}
								onChange={(e) => setDiscount(e.target.value)}
								placeholder="e.g. 30 (leave empty to clear)"
							/>
						</div>
					)}

					{mode === "override" && (
						<>
							<div>
								<Label>Column</Label>
								<Select value={columnKey} onValueChange={setColumnKey}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{columns.map((c) => (
											<SelectItem key={c.key} value={c.key}>
												{c.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div>
								<Label htmlFor="bulk-col-value">New value</Label>
								<Input
									id="bulk-col-value"
									value={columnValue}
									onChange={(e) => setColumnValue(e.target.value)}
									placeholder="Leave empty to clear override"
								/>
							</div>
						</>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={handleApply} disabled={saving}>
						{saving && <RiLoader4Line className="size-4 mr-1.5 animate-spin" />}
						Apply to {ids.length} rows
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Audit history
// ---------------------------------------------------------------------------

interface AuditEntry {
	id: string;
	userId: string | null;
	action: string;
	affectedCount: number;
	details: Record<string, unknown>;
	createdAt: string;
}

interface AuditResponse {
	entries: AuditEntry[];
	totalCount: number;
	page: number;
	pageSize: number;
}

const ACTION_LABELS: Record<string, string> = {
	single_edit: "Single edit",
	bulk_update: "Bulk update",
	exclude: "Exclude from export",
	revert: "Revert override",
};

function HistoryDialog({ onClose }: { onClose: () => void }) {
	const [page, setPage] = useState(1);
	const pageSize = 50;

	const { data, isLoading } = $api.useQuery(
		"get",
		"/api/v1/catalogue/audit-log" as never,
		{ params: { query: { page, pageSize } } } as never,
	);
	const response = data as AuditResponse | undefined;
	const entries = response?.entries ?? [];
	const total = response?.totalCount ?? 0;
	const totalPages = Math.ceil(total / pageSize);

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Catalogue history</DialogTitle>
					<DialogDescription>
						All edits made to catalogue rows, most recent first.
					</DialogDescription>
				</DialogHeader>

				{isLoading ? (
					<div className="py-8 flex justify-center">
						<RiLoader4Line className="size-5 animate-spin text-muted-foreground" />
					</div>
				) : entries.length === 0 ? (
					<div className="py-8 text-center text-sm text-muted-foreground">No activity yet.</div>
				) : (
					<div className="space-y-2">
						{entries.map((entry) => (
							<div key={entry.id} className="border rounded-md p-3 text-sm space-y-1">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<Badge variant="secondary">{ACTION_LABELS[entry.action] ?? entry.action}</Badge>
										<span className="text-xs text-muted-foreground">
											{entry.affectedCount} row
											{entry.affectedCount === 1 ? "" : "s"}
										</span>
									</div>
									<span className="text-xs text-muted-foreground">
										{new Date(entry.createdAt).toLocaleString()}
									</span>
								</div>
								<AuditDetails details={entry.details} />
							</div>
						))}
					</div>
				)}

				{totalPages > 1 && (
					<DialogFooter className="flex items-center justify-between">
						<span className="text-xs text-muted-foreground">
							Page {page} of {totalPages}
						</span>
						<div className="flex gap-2">
							<Button
								variant="outline"
								size="sm"
								disabled={page === 1}
								onClick={() => setPage(page - 1)}
							>
								Previous
							</Button>
							<Button
								variant="outline"
								size="sm"
								disabled={page >= totalPages}
								onClick={() => setPage(page + 1)}
							>
								Next
							</Button>
						</div>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);
}

function AuditDetails({ details }: { details: Record<string, unknown> }) {
	const parts: string[] = [];
	if (details.commercialDiscount != null) {
		parts.push(`discount = ${details.commercialDiscount}%`);
	}
	if (details.clearCommercialDiscount === true) {
		parts.push("cleared discount");
	}
	if (details.exportEnabled === true) parts.push("marked for export");
	if (details.exportEnabled === false) parts.push("removed from export");
	if (details.overrides && typeof details.overrides === "object") {
		const keys = Object.keys(details.overrides as Record<string, unknown>);
		if (keys.length > 0) {
			parts.push(`override: ${keys.join(", ")}`);
		}
	}
	if (parts.length === 0) {
		return null;
	}
	return <div className="text-xs text-muted-foreground">{parts.join(" · ")}</div>;
}

// ---------------------------------------------------------------------------
// Image gallery — embedded + stand-alone dialog
// ---------------------------------------------------------------------------

interface ArticleImage {
	id: string;
	articleId: string;
	fileUrl: string;
	contentType: string;
	byteSize: number;
	isPrimary: boolean;
	sortOrder: number;
	uploadedAt: string;
}

function ArticleImageGallery({ articleId }: { articleId: string }) {
	const queryClient = useQueryClient();
	const [uploading, setUploading] = useState(false);

	const { data } = $api.useQuery(
		"get",
		"/api/v1/catalogue/articles/{article_id}/images" as never,
		{ params: { path: { article_id: articleId } } } as never,
	);
	const images = (data as ArticleImage[] | undefined) ?? [];

	function invalidate() {
		queryClient.invalidateQueries({
			queryKey: [
				"get",
				"/api/v1/catalogue/articles/{article_id}/images",
				{ params: { path: { article_id: articleId } } },
			],
		});
	}

	async function handleFiles(files: FileList | null) {
		if (!files || files.length === 0) return;
		setUploading(true);
		try {
			for (const file of Array.from(files)) {
				const form = new FormData();
				form.append("file", file);
				const res = await fetch(`/api/v1/catalogue/articles/${articleId}/images`, {
					method: "POST",
					credentials: "include",
					body: form,
				});
				if (!res.ok) {
					toast.error(`Upload failed for ${file.name}`);
				}
			}
			toast.success("Uploaded");
			invalidate();
		} finally {
			setUploading(false);
		}
	}

	async function setPrimary(id: string) {
		const res = await fetch(`/api/v1/catalogue/images/${id}/primary`, {
			method: "PATCH",
			credentials: "include",
		});
		if (!res.ok) {
			toast.error("Failed to set primary");
			return;
		}
		invalidate();
	}

	async function deleteImage(id: string) {
		const res = await fetch(`/api/v1/catalogue/images/${id}`, {
			method: "DELETE",
			credentials: "include",
		});
		if (!res.ok) {
			toast.error("Delete failed");
			return;
		}
		invalidate();
	}

	return (
		<div>
			<Label>Images</Label>
			<div className="mt-1 grid grid-cols-4 gap-2">
				{images.map((img) => (
					<div
						key={img.id}
						className={cn(
							"relative aspect-square border rounded overflow-hidden group",
							img.isPrimary && "ring-2 ring-primary",
						)}
					>
						<img src={img.fileUrl} alt="" className="object-cover w-full h-full" />
						{img.isPrimary && <Badge className="absolute top-1 left-1 text-[10px]">Primary</Badge>}
						<div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
							{!img.isPrimary && (
								<Button size="xs" variant="secondary" onClick={() => setPrimary(img.id)}>
									Primary
								</Button>
							)}
							<Button size="xs" variant="destructive" onClick={() => deleteImage(img.id)}>
								Delete
							</Button>
						</div>
					</div>
				))}
				<label
					className={cn(
						"aspect-square border-2 border-dashed rounded flex items-center justify-center text-xs text-muted-foreground cursor-pointer hover:border-primary/50 hover:text-foreground transition",
						uploading && "opacity-50 pointer-events-none",
					)}
				>
					{uploading ? <RiLoader4Line className="size-5 animate-spin" /> : <>+ Upload</>}
					<input
						type="file"
						accept="image/*"
						multiple
						className="hidden"
						onChange={(e) => handleFiles(e.target.files)}
					/>
				</label>
			</div>
		</div>
	);
}

function ImagesDialog({
	articleId,
	articleNumber,
	onClose,
}: {
	articleId: string;
	articleNumber: string;
	onClose: () => void;
}) {
	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Images — {articleNumber}</DialogTitle>
					<DialogDescription>
						The primary image is used as the Farfetch Image URL for all sizes of this article.
					</DialogDescription>
				</DialogHeader>
				<ArticleImageGallery articleId={articleId} />
				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// ---------------------------------------------------------------------------
// Brand default discounts dialog
// ---------------------------------------------------------------------------

interface BrandDiscount {
	id: string;
	brandName: string;
	discount: number;
	updatedAt: string;
}

interface BrandOption {
	name: string;
	articleCount: number;
}

function BrandDiscountsDialog({
	onClose,
	onChanged,
}: {
	onClose: () => void;
	onChanged: () => void;
}) {
	const queryClient = useQueryClient();
	const [brandName, setBrandName] = useState("");
	const [discount, setDiscount] = useState("");
	const [saving, setSaving] = useState(false);

	const { data, isLoading } = $api.useQuery("get", "/api/v1/catalogue/brand-discounts" as never);
	const rows = (data as BrandDiscount[] | undefined) ?? [];

	const { data: brandsData } = $api.useQuery("get", "/api/v1/catalogue/brands" as never);
	const brands = (brandsData as BrandOption[] | undefined) ?? [];

	function invalidate() {
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/catalogue/brand-discounts"],
		});
		onChanged();
	}

	async function handleAdd() {
		const d = Number(discount);
		if (!brandName.trim() || Number.isNaN(d)) {
			toast.error("Enter a brand and a numeric discount");
			return;
		}
		setSaving(true);
		try {
			const { error } = await fetchClient.POST(
				"/api/v1/catalogue/brand-discounts" as never,
				{ body: { brandName: brandName.trim(), discount: d } } as never,
			);
			if (error) {
				toast.error("Save failed");
				return;
			}
			setBrandName("");
			setDiscount("");
			invalidate();
			toast.success("Default saved");
		} finally {
			setSaving(false);
		}
	}

	async function handleDelete(id: string) {
		const { error } = await fetchClient.DELETE(
			"/api/v1/catalogue/brand-discounts/{id}" as never,
			{ params: { path: { id } } } as never,
		);
		if (error) {
			toast.error("Delete failed");
			return;
		}
		invalidate();
	}

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Per-brand default discounts</DialogTitle>
					<DialogDescription>
						Rows with no row-level discount fall back to their brand's default at export time.
						Row-level edits always win.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div className="flex gap-2 items-end">
						<div className="flex-1">
							<Label htmlFor="bd-brand">Brand</Label>
							{brands.length > 0 ? (
								<Select value={brandName} onValueChange={setBrandName}>
									<SelectTrigger id="bd-brand">
										<SelectValue placeholder="Choose a brand" />
									</SelectTrigger>
									<SelectContent>
										{brands.map((b) => (
											<SelectItem key={b.name} value={b.name}>
												{b.name}{" "}
												<span className="text-muted-foreground text-xs ml-1">
													({b.articleCount})
												</span>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							) : (
								<Input
									id="bd-brand"
									value={brandName}
									onChange={(e) => setBrandName(e.target.value)}
									placeholder="e.g. PRADA BV."
								/>
							)}
						</div>
						<div className="w-28">
							<Label htmlFor="bd-discount">Discount %</Label>
							<Input
								id="bd-discount"
								type="number"
								min={0}
								max={100}
								step={0.1}
								value={discount}
								onChange={(e) => setDiscount(e.target.value)}
								placeholder="30"
							/>
						</div>
						<Button onClick={handleAdd} disabled={saving}>
							{saving && <RiLoader4Line className="size-4 mr-1.5 animate-spin" />}
							Save
						</Button>
					</div>

					<div className="border rounded-md">
						{isLoading ? (
							<div className="p-4 text-sm text-muted-foreground">Loading...</div>
						) : rows.length === 0 ? (
							<div className="p-4 text-sm text-muted-foreground">No brand defaults yet.</div>
						) : (
							<ul className="divide-y">
								{rows.map((r) => (
									<li key={r.id} className="flex items-center justify-between px-3 py-2 text-sm">
										<span className="font-medium">{r.brandName}</span>
										<div className="flex items-center gap-3">
											<span className={cn("tabular-nums", discountColor(r.discount))}>
												{r.discount.toFixed(1)}%
											</span>
											<Button variant="ghost" size="xs" onClick={() => handleDelete(r.id)}>
												<RiDeleteBinLine className="size-3" />
											</Button>
										</div>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
